import {
  CorrectiveActionsRequest,
  CorrectiveActionsStatsRequest,
  DownloadSubmittedFormPdfRequest,
  GetCorrectiveActionRequest,
  UpdateCorrectiveActionRequest,
  ViewSubmittedFormPdfRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { RequestPagination } from '~/common/types';
import {
  CorrectiveAction,
  CorrectiveActionForm,
} from '~/correctiveActions/types';
import { getCorrectiveActionStatus } from '../composables/correctiveAction';

export class CorrectiveActionService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async fetch(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new CorrectiveActionsRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    const { data, meta } = await this.apiClient.request(request);

    const correctiveActions: CorrectiveAction[] = data.map(
      (correctiveAction: CorrectiveAction): CorrectiveAction => {
        return this.transform(correctiveAction);
      },
    );
    return { data: correctiveActions, meta };
  }

  async stats() {
    const accessToken = this.userStore.accessToken;
    const request = new CorrectiveActionsStatsRequest({
      token: accessToken,
    });
    return this.apiClient.request(request);
  }

  async get(correctiveActionId: string, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new GetCorrectiveActionRequest({
      token: accessToken,
      correctiveActionId,
      params: { ...params },
    });

    const { data } = await this.apiClient.request(request);
    return this.transform(data);
  }

  async update(
    correctiveActionId: string,
    correctiveAction: CorrectiveActionForm,
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateCorrectiveActionRequest({
      token: accessToken,
      correctiveActionId,
      correctiveAction,
    });

    return this.apiClient.request(request);
  }

  async viewSubmittedFormPdf(submittedFormID: string): Promise<Blob> {
    const request = new ViewSubmittedFormPdfRequest({
      token: this.userStore.accessToken,
      submittedFormID,
    });
    return this.apiClient.request(request, { responseType: 'blob' });
  }

  async downloadSubmittedFormPdf(submittedFormID: string): Promise<Blob> {
    const request = new DownloadSubmittedFormPdfRequest({
      token: this.userStore.accessToken,
      submittedFormID,
    });
    return this.apiClient.request(request, { responseType: 'blob' });
  }

  transform(correctiveAction: CorrectiveAction) {
    const {
      assignDate,
      assignedUserID,
      assignedUsers,
      comments,
      createdBy,
      description,
      eventID,
      id,
      name,
      reportDate,
      requiredApprovals,
      resolveDate,
      resolved,
      resolvedBy,
      resolvedFormID,
      sourceID,
      sourceType,
      submittedFormID,
      targetDate,
      worksiteID,
      submittedForm,
      worksite,
    } = correctiveAction;

    let formattedName = '';

    if (name) formattedName = useFormatString(name as string);

    const status = getCorrectiveActionStatus({ resolved, targetDate }).label;

    return {
      assignDate,
      assignedUserID,
      assignedUsers,
      createdBy,
      isAssigned: assignedUsers?.data?.length ? 'Yes' : 'No',
      comments,
      description,
      eventID,
      id,
      name: formattedName,
      reportDate,
      requiredApprovals,
      resolveDate: useFormatDateShort(resolveDate as string),
      resolved,
      resolvedBy,
      resolvedFormID,
      sourceID,
      sourceType,
      submittedFormID,
      targetDate: useFormatDateShort(targetDate as string),
      worksiteID,
      status,
      submittedForm,
      worksite,
    };
  }
}
